// Style
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import React, { Component, Suspense } from "react";
// Redux
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Link, Redirect, Route } from "react-router-dom";
import { bindActionCreators } from "redux";

import { ActionCreators } from "../../actions";
import * as globalStyles from "../../utils/GlobalStyles";
import { lazyWithRetry } from "../../utils/ProgUtil";
import { theme } from "../../utils/Theme";
import SelectableButtonGroupView from "../organisms/SelectableButtonGroupView";
import {
  HelpPageView,
  isPublicRoutePath,
  LetterInvitePageView,
  LandingPageView,
  MemberInvitePageView,
  PrivacyPolicyView,
  SafetyPolicyView,
} from "../pages/PublicPagesView";

// Pages
const AdminNestView = lazyWithRetry(() => import("../pages/AdminNestView"));
const AdminCouponsView = lazyWithRetry(() =>
  import("../pages/AdminCouponsView"),
);
const AdminMapView = lazyWithRetry(() => import("../pages/AdminMapView"));
const LoginView = lazyWithRetry(() => import("../pages/LoginView"));
const AdminMembersView = lazyWithRetry(() => import("../pages/AdminMembersView"));
const AdminPenpalView = lazyWithRetry(() => import("../pages/AdminPenpalView"));
const AdminStatsView = lazyWithRetry(() => import("../pages/AdminStatsView"));

class AppView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedButtonId: this.getSelectedButtonIdFromPath(
        props.location?.pathname,
      ),
      viewportWidth: typeof window === "undefined" ? 1200 : window.innerWidth,
    };
  }

  adminOptions = [
    { name: "Nest", id: "nest", route: "/nest" },
    {
      name: "Stats",
      id: "stats",
      route: "/stats",
    },
    {
      name: "Members",
      id: "members",
      route: "/members",
    },
    {
      name: "Pen Pals",
      id: "penpals",
      route: "/penpals",
    },
    { name: "Coupons", id: "coupons", route: "/coupons" },
    {
      name: "Air Traffic Control",
      id: "airtrafficcontrol",
      route: "/airtrafficcontrol",
    },
  ];

  isPublicPath = (pathname = "") => isPublicRoutePath(pathname);

  isLoggedInAsAdmin = () =>
    !!this.props.login?.authToken && !!this.props.login?.isAdmin;

  getLoginRedirectRoute = (location = {}) => {
    const requestedRoute = `${location.pathname || ""}${location.search || ""}`;
    return `/login?redirect=${encodeURIComponent(requestedRoute || "/nest")}`;
  };

  getOptionsFromPath = (pathname = "") =>
    this.isPublicPath(pathname) ? [] : this.adminOptions;

  getSelectedButtonIdFromPath = (pathname = "") => {
    const options = this.getOptionsFromPath(pathname);
    const matchingOption = options.find((option) => option.route === pathname);
    return matchingOption?.id || options[0]?.id;
  };

  getViewportWidth = () =>
    typeof window === "undefined" ? 1200 : window.innerWidth;

  updateViewportWidth = () => {
    this.setState({ viewportWidth: this.getViewportWidth() });
  };

  componentDidMount() {
    window.addEventListener("resize", this.updateViewportWidth);
    const selectedButtonId = this.getSelectedButtonIdFromPath(
      this.props.location?.pathname,
    );
    if (selectedButtonId !== this.state.selectedButtonId) {
      this.setState({ selectedButtonId });
    }
    this.handleLogoutRoute();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateViewportWidth);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.pathname === this.props.location?.pathname) return;

    const selectedButtonId = this.getSelectedButtonIdFromPath(
      this.props.location?.pathname,
    );
    if (selectedButtonId !== this.state.selectedButtonId) {
      this.setState({ selectedButtonId });
    }
    this.handleLogoutRoute();
  }

  handleLogoutRoute = () => {
    if (this.props.location?.pathname !== "/logout") return;

    this.props.logout();
    this.props.history.replace("/welcome");
  };

  handleButtonSelect = (buttonId) => {
    const options = this.getOptionsFromPath(this.props.location?.pathname);
    this.props.history.push(
      options.find((option) => option.id === buttonId).route,
    );
  };

  renderPublicHeader = () => (
    <div
      style={{
        ...styles.publicHeader,
        ...(this.state.viewportWidth < 620 ? styles.publicHeaderMobile : {}),
      }}
    >
      <Link style={styles.publicBrand} to="/">
        <img
          alt=""
          aria-hidden="true"
          src="/raven-assets/app-icon.png"
          style={styles.publicBrandIcon}
        />
        <span>Raven</span>
      </Link>
    </div>
  );

  renderAdminHeader = (routeOptions) => (
    <div style={styles.buttonGroup}>
      <SelectableButtonGroupView
        options={routeOptions}
        selectedOptionId={this.state.selectedButtonId}
        onSelect={this.handleButtonSelect}
        isScrollable
      />
    </div>
  );

  renderRootRoute = () => (
    <Redirect to={this.isLoggedInAsAdmin() ? "/nest" : "/welcome"} />
  );

  renderProtectedRoute = ({ component: ProtectedComponent, ...routeProps }) => (
    <Route
      {...routeProps}
      render={(props) =>
        this.isLoggedInAsAdmin() ? (
          <ProtectedComponent {...props} />
        ) : (
          <Redirect to={this.getLoginRedirectRoute(props.location)} />
        )
      }
    />
  );

  render() {
    const isPublicPath = this.isPublicPath(this.props.location?.pathname);
    const routeOptions = this.getOptionsFromPath(this.props.location?.pathname);

    return (
      <StyledEngineProvider injectFirst>
        {" "}
        {/* From MUI5 migration*/}
        <MuiThemeProvider theme={theme}>
          <div style={isPublicPath ? styles.publicPage : styles.page}>
            {isPublicPath
              ? this.renderPublicHeader()
              : this.renderAdminHeader(routeOptions)}

            <Route exact path="/" render={this.renderRootRoute} />
            <Route exact path="/welcome" component={LandingPageView} />
            <Route exact path="/safety-policy" component={SafetyPolicyView} />
            <Route exact path="/privacy-policy" component={PrivacyPolicyView} />
            <Route exact path="/help" component={HelpPageView} />
            <Route exact path="/l/:letterToken" component={LetterInvitePageView} />
            <Route exact path="/letter/:letterToken" component={LetterInvitePageView} />
            <Route exact path="/i/:inviteToken" component={MemberInvitePageView} />

            <Suspense fallback={<div />}>
              <Route exact path="/login" component={LoginView} />
              <Route exact path="/logout" render={() => null} />
              {this.renderProtectedRoute({
                exact: true,
                path: "/nest",
                component: AdminNestView,
              })}
              {this.renderProtectedRoute({
                exact: true,
                path: "/coupons",
                component: AdminCouponsView,
              })}
              {this.renderProtectedRoute({
                exact: true,
                path: "/airtrafficcontrol",
                component: AdminMapView,
              })}
              {this.renderProtectedRoute({
                exact: true,
                path: "/members",
                component: AdminMembersView,
              })}
              {this.renderProtectedRoute({
                exact: true,
                path: "/penpals",
                component: AdminPenpalView,
              })}
              {this.renderProtectedRoute({
                exact: true,
                path: "/stats",
                component: AdminStatsView,
              })}
            </Suspense>
          </div>
        </MuiThemeProvider>
      </StyledEngineProvider>
    );
  }
}

const styles = {
  page: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
    overflowX: "hidden",
    backgroundColor: globalStyles.colors.base1,
  },
  publicPage: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
    overflowX: "hidden",
    backgroundColor: "#101114",
  },
  publicHeader: {
    display: "flex",
    alignItems: "center",
    minHeight: 72,
    width: "100%",
    padding: "16px 42px",
    background: "rgba(16, 17, 20, 0.82)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.09)",
    backdropFilter: "blur(18px)",
    flexShrink: 0,
    boxSizing: "border-box",
  },
  publicHeaderMobile: {
    minHeight: 70,
    padding: "16px 18px",
  },
  publicBrand: {
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    color: "#ffffff",
    fontSize: 19,
    fontWeight: 800,
    textDecoration: "none",
    flexShrink: 0,
  },
  publicBrandIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.22)",
    objectFit: "cover",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    flexDirection: "column",
    zIndex: "10",
  },
  contentAndMessages: {
    flex: 1,
    overflowY: "auto", // we may want overflowX also, but that can be handled when we do responsiveness
    backgroundColor: globalStyles.colors.base1,
  },
  contentStyle: {
    paddingTop: 24,
  },
  login: {
    justifyContent: "center",
  },
  buttonGroup: {
    marginTop: 24,
    maxWidth: "100%",
    overflowX: "hidden",
    flexShrink: 0,
  },
};

function mapStateToProps(state) {
  return {
    login: state.login,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppView),
);
